import React, { useEffect, useMemo } from "react";
import { AiOutlineDollar, AiOutlineOrderedList, AiOutlineShop } from "react-icons/ai";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllOrdersOfAdmin } from "../../redux/actions/order";
import { getAllSellers } from "../../redux/actions/sellers";
import Loader from "../Layout/Loader";
import AdminVisualDesignLibrary from "./AdminVisualDesignLibrary";
import AdminPlatformIdentityAudit from "./AdminPlatformIdentityAudit";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Filler, Title, Tooltip, Legend);

const AdminDashboardMain = () => {
  const [searchParams] = useSearchParams();
  const activeView = searchParams.get("view");
  const isUxLibraryView = activeView === "ux-library";
  const isPlatformIdentityView = activeView === "platform-identity";
  const dispatch = useDispatch();
  const { adminOrders, adminOrderLoading, adminOrderError } = useSelector((state) => state.order);
  const { sellers } = useSelector((state) => state.seller);
  const safeAdminOrders = useMemo(() => (Array.isArray(adminOrders) ? adminOrders : []), [adminOrders]);
  const safeSellers = useMemo(() => (Array.isArray(sellers) ? sellers : []), [sellers]);

  useEffect(() => {
    if (isUxLibraryView || isPlatformIdentityView) {
      return;
    }

    dispatch(getAllOrdersOfAdmin());
    dispatch(getAllSellers());
  }, [dispatch, isPlatformIdentityView, isUxLibraryView]);

  const adminEarning = safeAdminOrders.reduce((acc, item) => acc + (Number(item.totalPrice) || 0) * 0.1, 0);
  const adminBalance = adminEarning.toFixed(2);

  const orderPulse = useMemo(() => {
    const summaryByDate = new Map();

    safeAdminOrders.forEach((order) => {
      const orderDate = new Date(order.createdAt);

      if (Number.isNaN(orderDate.getTime())) {
        return;
      }

      const dateKey = orderDate.toISOString().slice(0, 10);
      const existingSummary = summaryByDate.get(dateKey) || {
        label: orderDate.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        orders: 0,
        revenue: 0,
      };

      existingSummary.orders += 1;
      existingSummary.revenue += Number(order.totalPrice) || 0;
      summaryByDate.set(dateKey, existingSummary);
    });

    return [...summaryByDate.entries()]
      .sort(([firstDate], [secondDate]) => new Date(firstDate) - new Date(secondDate))
      .slice(-7)
      .map(([, summary]) => summary);
  }, [safeAdminOrders]);

  const statusPulse = useMemo(() => {
    const statusCounts = safeAdminOrders.reduce((counts, order) => {
      const status = order.status || "Unknown";
      counts[status] = (counts[status] || 0) + 1;
      return counts;
    }, {});

    return Object.entries(statusCounts).sort(([, firstCount], [, secondCount]) => secondCount - firstCount);
  }, [safeAdminOrders]);

  const lastSevenDayRevenue = orderPulse.reduce((total, item) => total + item.revenue, 0);
  const lastSevenDayOrders = orderPulse.reduce((total, item) => total + item.orders, 0);
  const pendingOrders = safeAdminOrders.filter((order) => order.status !== "Delivered").length;

  const liveChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: "rgba(255, 255, 255, 0.78)", boxWidth: 10 },
      },
      title: { display: false },
    },
    scales: {
      x: {
        grid: { color: "rgba(255, 255, 255, 0.08)" },
        ticks: { color: "rgba(255, 255, 255, 0.68)" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(255, 255, 255, 0.08)" },
        ticks: { color: "rgba(255, 255, 255, 0.68)" },
      },
    },
  };

  const revenueTrendData = {
    labels: orderPulse.map((item) => item.label),
    datasets: [
      {
        label: "Revenue",
        data: orderPulse.map((item) => Number(item.revenue.toFixed(2))),
        borderColor: "#36d300",
        backgroundColor: "rgba(54, 211, 0, 0.14)",
        fill: true,
        tension: 0.38,
      },
    ],
  };

  const orderStatusData = {
    labels: statusPulse.map(([status]) => status),
    datasets: [
      {
        label: "Orders",
        data: statusPulse.map(([, count]) => count),
        backgroundColor: ["rgba(54, 211, 0, 0.58)", "rgba(253, 131, 9, 0.58)", "rgba(59, 130, 246, 0.58)", "rgba(239, 68, 68, 0.58)"],
        borderColor: ["rgba(54, 211, 0, 0.86)", "rgba(253, 131, 9, 0.86)", "rgba(59, 130, 246, 0.86)", "rgba(239, 68, 68, 0.86)"],
        borderWidth: 1,
      },
    ],
  };

  const columns = [
    { field: "id", headerName: "Order ID", minWidth: 150, flex: 0.7 },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 0.7,
      cellClassName: (params) => {
        return params.row.status === "Delivered" ? "greenColor" : "redColor";
      },
    },
    {
      field: "itemsQty",
      headerName: "Items Qty",
      type: "number",
      minWidth: 130,
      flex: 0.7,
    },
    {
      field: "total",
      headerName: "Total",
      type: "number",
      minWidth: 130,
      flex: 0.8,
    },
    {
      field: "createdAt",
      headerName: "Order Date",
      type: "number",
      minWidth: 130,
      flex: 0.8,
    },
  ];

  const rows = safeAdminOrders.map((item) => ({
    id: item._id,
    itemsQty: (Array.isArray(item.cart) ? item.cart : []).reduce((acc, cartItem) => acc + (Number(cartItem.qty) || 0), 0),
    total: `${Number(item.totalPrice) || 0} $`,
    status: item.status || "Unknown",
    createdAt: item.createdAt ? String(item.createdAt).slice(0, 10) : "Pending",
  }));

  if (isUxLibraryView) {
    return <AdminVisualDesignLibrary />;
  }

  if (isPlatformIdentityView) {
    return <AdminPlatformIdentityAudit />;
  }

  return (
    <>
      {adminOrderLoading ? (
        <Loader />
      ) : (
        <div className="admin-dashboard-main-container">
          <header className="admin-page-hero">
            <div>
              <p className="admin-page-eyebrow">Super Admin Overview</p>
              <h1>Dashboard</h1>
              <p className="admin-page-copy">Track marketplace totals, seller activity, and the latest order flow from one control surface.</p>
            </div>
          </header>

          {adminOrderError && (
            <div className="admin-dashboard-data-notice" role="alert">
              <span>{adminOrderError}</span>
              <button type="button" onClick={() => dispatch(getAllOrdersOfAdmin())}>Retry orders</button>
            </div>
          )}

          <div className="admin-dashboard-main-overview">
            <div className="admin-dashboard-main-card">
              <div className="admin-dashboard-main-card-content">
                <AiOutlineDollar size={30} className="admin-dashboard-main-icon" />
                <h3 className="admin-dashboard-main-card-title">Total Earning</h3>
              </div>
              <h5 className="admin-dashboard-main-card-value">$ {adminBalance}</h5>
            </div>

            <div className="admin-dashboard-main-card">
              <div className="admin-dashboard-main-card-content">
                <AiOutlineShop size={30} className="admin-dashboard-main-icon" />
                <h3 className="admin-dashboard-main-card-title">All Sellers</h3>
              </div>
              <h5 className="admin-dashboard-main-card-value">{safeSellers.length}</h5>
              <Link to="/admin-sellers" className="admin-dashboard-main-card-link">View Sellers</Link>
            </div>

            <div className="admin-dashboard-main-card">
              <div className="admin-dashboard-main-card-content">
                <AiOutlineOrderedList size={30} className="admin-dashboard-main-icon" />
                <h3 className="admin-dashboard-main-card-title">All Orders</h3>
              </div>
              <h5 className="admin-dashboard-main-card-value">{safeAdminOrders.length}</h5>
              <Link to="/admin-orders" className="admin-dashboard-main-card-link">View Orders</Link>
            </div>
          </div>

          <section className="admin-dashboard-live-grid" aria-label="Live marketplace dashboard views">
            <article className="admin-dashboard-live-card">
              <header className="admin-dashboard-live-card-header">
                <p>Revenue Pulse</p>
                <h2>Last 7 Active Days</h2>
              </header>
              <div className="admin-dashboard-live-chart">
                <Line data={revenueTrendData} options={liveChartOptions} />
              </div>
            </article>

            <article className="admin-dashboard-live-card">
              <header className="admin-dashboard-live-card-header">
                <p>Order Mix</p>
                <h2>Status Snapshot</h2>
              </header>
              <div className="admin-dashboard-live-chart">
                <Bar data={orderStatusData} options={liveChartOptions} />
              </div>
            </article>

            <article className="admin-dashboard-live-card admin-dashboard-live-card--metrics">
              <header className="admin-dashboard-live-card-header">
                <p>Marketplace Pulse</p>
                <h2>Live Totals</h2>
              </header>
              <div className="admin-dashboard-live-metric-list">
                <div>
                  <span>7-Day Orders</span>
                  <strong>{lastSevenDayOrders}</strong>
                </div>
                <div>
                  <span>7-Day Revenue</span>
                  <strong>${lastSevenDayRevenue.toFixed(2)}</strong>
                </div>
                <div>
                  <span>Open Orders</span>
                  <strong>{pendingOrders}</strong>
                </div>
                <div>
                  <span>Active Sellers</span>
                  <strong>{safeSellers.length}</strong>
                </div>
              </div>
            </article>
          </section>

          <div className="admin-dashboard-orders__section">
              <h2 className="admin-dashboard-orders__title">Latest Orders</h2>
              <div className="admin-dashboard-orders__table-container">
                <table className="admin-dashboard-orders__table">
                  <thead className="admin-dashboard-orders__thead">
                    <tr>
                      {columns.map((column) => (
                        <th key={column.field} style={{ minWidth: column.minWidth }}>
                          {column.headerName}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="admin-dashboard-orders__tbody">
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        className="admin-dashboard-orders__tr"
                      >
                        <td>{row.id}</td>
                        <td
                          className={`admin-dashboard-orders__status ${
                            row.status === "Delivered"
                              ? "admin-dashboard-orders__status--delivered"
                              : "admin-dashboard-orders__status--pending"
                          }`}
                        >
                          {row.status}
                        </td>
                        <td>{row.itemsQty}</td>
                        <td>{row.total}</td>
                        <td>{row.createdAt}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
        </div>
      )}
    </>
  );
};

export default AdminDashboardMain;